import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Pill, ArrowRight, Eye, EyeOff, Gift, CheckCircle2, XCircle } from "lucide-react";
import { HIPAABadge } from "@/components/HIPAACompliance";
import WaitlistGate from "@/components/WaitlistGate";
import { useWaitlistStatus } from "@/hooks/useWaitlistStatus";

export default function PatientRegister() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const waitlist = useWaitlistStatus();
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", referral_code: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [contactConsent, setContactConsent] = useState(false);
  const [refStatus, setRefStatus] = useState({ checking: false, valid: null, referrerName: "" });

  // Some pages (dialogs, sheets) lock `body.style.overflow = 'hidden'` and
  // occasionally forget to clear it on unmount, which strands /register with
  // no scrollbar and the submit button pinned below the fold. Reset every
  // ancestor scroll-lock on mount so the form is always fully reachable.
  useEffect(() => {
    document.documentElement.style.overflowY = "auto";
    document.documentElement.style.height = "auto";
    document.body.style.overflow = "";
    document.body.style.overflowY = "auto";
    document.body.style.height = "auto";
    document.body.style.position = "";
  }, []);

  // Prefill referral code from ?ref=... query param + capture ?promo=... tag
  useEffect(() => {
    const refFromUrl = searchParams.get("ref");
    if (refFromUrl) {
      setForm((f) => ({ ...f, referral_code: refFromUrl.toUpperCase() }));
    }
    const promoFromUrl = (searchParams.get("promo") || "").trim().toLowerCase();
    if (promoFromUrl) {
      try { localStorage.setItem("glpflo_promo_src", promoFromUrl); } catch { /* noop */ }
    }
  }, [searchParams]);

  // Validate referral code when it reaches 8 chars
  useEffect(() => {
    const code = (form.referral_code || "").trim().toUpperCase();
    if (code.length !== 8) { setRefStatus({ checking: false, valid: null, referrerName: "" }); return; }
    let cancelled = false;
    setRefStatus({ checking: true, valid: null, referrerName: "" });
    API.get(`/referrals/validate/${code}`).then(({ data }) => {
      if (cancelled) return;
      setRefStatus({ checking: false, valid: !!data.valid, referrerName: data.referrer_name || "" });
    }).catch(() => {
      if (!cancelled) setRefStatus({ checking: false, valid: false, referrerName: "" });
    });
    return () => { cancelled = true; };
  }, [form.referral_code]);

  const handleChange = (e) => {
    const v = e.target.name === "referral_code" ? e.target.value.toUpperCase() : e.target.value;
    setForm({ ...form, [e.target.name]: v });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 8) { setError("Password must be at least 8 characters"); return; }
    if (!/[A-Z]/.test(form.password)) { setError("Password must contain at least one uppercase letter"); return; }
    if (!/[!@#$%^&*()_+\-=\[\]{}|;':",./<>?]/.test(form.password)) { setError("Password must contain at least one special character"); return; }
    if (!contactConsent) { setError("You must agree to the HIPAA-compliant contact consent"); return; }
    if (form.referral_code && refStatus.valid === false) { setError("Please clear the invalid referral code or enter a valid one"); return; }
    setLoading(true);
    const payload = { ...form, contact_consent: contactConsent };
    if (!payload.referral_code) delete payload.referral_code;
    try {
      const storedSrc = localStorage.getItem("glpflo_share_src");
      if (storedSrc) payload.share_src = storedSrc;
      const storedPromo = localStorage.getItem("glpflo_promo_src");
      if (storedPromo) payload.promo_src = storedPromo;
    } catch (_e) { /* ignore — private mode etc. */ }
    try {
      const { executeCaptcha } = await import("@/lib/recaptcha");
      payload.captcha_token = await executeCaptcha("signup");
      payload.captcha_version = "v3";
    } catch (_e) { /* captcha disabled or failed — backend handles */ }
    const result = await register(payload);
    setLoading(false);
    if (result.success) {
      try {
        const { analytics } = await import("@/instrument");
        analytics.capture("registration_completed", {
          promo_src: payload.promo_src || null,
          has_referral: Boolean(payload.referral_code),
        });
      } catch (_e) { /* noop */ }
      try {
        localStorage.removeItem("glpflo_share_src");
        localStorage.removeItem("glpflo_promo_src");
      } catch (_e) { /* noop */ }
      window.location.href = "/onboarding";
    } else {
      setError(result.error);
    }
  };

  // Waitlist gate — super-admin can freeze new signups platform-wide.
  // Hook fails open so a network hiccup won't accidentally block real users.
  if (waitlist.loading) return null;
  if (waitlist.enabled) {
    return <WaitlistGate message={waitlist.message} source="patient_register" />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-100 flex flex-col items-center justify-start py-8 px-4" data-testid="patient-register-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center">
            <Pill className="w-5 h-5 text-white" />
          </div>
          <span className="font-bold text-2xl tracking-tight text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
            GLP<span className="text-purple-600">Flo</span>
          </span>
        </Link>

        <div className="flex justify-center mb-4">
          <HIPAABadge variant="pill" />
        </div>

        <div className="bg-white rounded-2xl border border-purple-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8">
          <h1 className="text-2xl font-semibold text-slate-900 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>Create Account</h1>
          <p className="text-slate-500 text-sm mb-6">Start your health journey with GLPFlo</p>

          {error && (
            <div className="bg-red-50 text-red-700 border border-red-200 rounded-xl px-4 py-3 text-sm mb-4" data-testid="register-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-sm font-medium text-slate-700">Full Name</Label>
              <Input id="name" name="name" value={form.name} onChange={handleChange} placeholder="John Doe"
                className="mt-1 rounded-xl" required data-testid="register-name-input" />
            </div>
            <div>
              <Label htmlFor="email" className="text-sm font-medium text-slate-700">Email</Label>
              <Input id="email" name="email" type="email" value={form.email} onChange={handleChange} placeholder="you@example.com"
                className="mt-1 rounded-xl" required data-testid="register-email-input" />
            </div>
            <div>
              <Label htmlFor="phone" className="text-sm font-medium text-slate-700">Phone (optional)</Label>
              <Input id="phone" name="phone" value={form.phone} onChange={handleChange} placeholder="(555) 123-4567"
                className="mt-1 rounded-xl" data-testid="register-phone-input" />
            </div>
            <div>
              <Label htmlFor="password" className="text-sm font-medium text-slate-700">Password</Label>
              <div className="relative mt-1">
                <Input id="password" name="password" type={showPw ? "text" : "password"} value={form.password} onChange={handleChange} placeholder="Min 8 chars, 1 uppercase, 1 special"
                  className="rounded-xl pr-10" required data-testid="register-password-input" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Referral code (optional) */}
            <div>
              <Label htmlFor="referral_code" className="text-sm font-medium text-slate-700 flex items-center gap-1">
                <Gift className="w-3.5 h-3.5 text-purple-600" /> Referral Code (optional)
              </Label>
              <div className="relative mt-1">
                <Input id="referral_code" name="referral_code" value={form.referral_code} onChange={handleChange}
                  placeholder="e.g. ALICE123" maxLength={8}
                  className={`rounded-xl uppercase tracking-widest pr-10 ${refStatus.valid === true ? "border-emerald-500" : refStatus.valid === false ? "border-red-400" : ""}`}
                  data-testid="register-referral-input" />
                {refStatus.checking && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
                )}
                {refStatus.valid === true && (
                  <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-600" />
                )}
                {refStatus.valid === false && form.referral_code.length === 8 && (
                  <XCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-red-500" />
                )}
              </div>
              {refStatus.valid === true && (
                <p className="text-xs text-emerald-700 mt-1.5" data-testid="referral-valid">
                  Referred by <strong>{refStatus.referrerName}</strong>. You&apos;ll both get $20 off when you complete your first order.
                </p>
              )}
              {refStatus.valid === false && form.referral_code.length === 8 && (
                <p className="text-xs text-red-600 mt-1.5" data-testid="referral-invalid">Invalid or expired referral code.</p>
              )}
            </div>

            {/* HIPAA Contact Consent */}
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4" data-testid="contact-consent-section">
              <p className="text-xs text-slate-600 leading-relaxed mb-3">
                By providing your email and phone number, you acknowledge that your prescriber and our partner pharmacy may contact you via these methods for prescription-related communications, order updates, and shipping/tracking information. All communications are conducted in compliance with HIPAA regulations and your information will never be shared with unauthorized parties.
              </p>
              <div className="flex items-start gap-3">
                <input type="checkbox" id="contact-consent" checked={contactConsent} onChange={(e) => setContactConsent(e.target.checked)}
                  className="mt-0.5 rounded border-slate-300 text-purple-600 focus:ring-purple-500" data-testid="contact-consent-checkbox" />
                <label htmlFor="contact-consent" className="text-sm text-slate-700 cursor-pointer leading-snug">
                  I agree that my prescriber and pharmacy may contact me via the email and phone number provided for prescription, tracking, and healthcare-related communications. <span className="text-purple-600 font-medium">HIPAA Compliant</span>
                </label>
              </div>
            </div>

            {/* Identity & Anti-Fraud Disclaimer */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="identity-disclaimer-section">
              <p className="text-xs text-amber-900 leading-relaxed font-semibold mb-1">
                Identity & accuracy disclaimer
              </p>
              <p className="text-xs text-amber-800 leading-relaxed">
                I confirm that the name, date of birth and address I provide are my own legal identifying details.
                Submitting fake names, fictitious identities or stolen addresses to obtain discounts, referrals or
                prescription medications is fraud and may result in account termination, forfeiture of any promotional
                credits, reporting to law enforcement, and permanent ineligibility for GLPFlo services. Referral rewards
                and other discounts are reserved for unique, verifiable customers — duplicate or mismatched addresses
                are automatically flagged for manual review.
              </p>
            </div>

            <Button type="submit" disabled={loading} className="w-full rounded-full bg-purple-600 hover:bg-purple-700 text-white py-6 text-base" data-testid="register-submit-button">
              {loading ? "Creating Account..." : "Create Account"} {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
            </Button>
          </form>

          <p className="text-sm text-slate-500 text-center mt-6">
            Already have an account?{" "}
            <Link to="/login" className="text-purple-600 hover:text-purple-700 font-medium" data-testid="register-login-link">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
